<script lang="ts">
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Hooks
	import { onMount } from 'svelte';
	//Services
	import { format } from '../../../../services/i18n';
	import { localReq, postReq } from '../../../../services/http';
	//Helpers
	import { get_local_ip, real_id } from '../../../../helpers/interface_helpers';
	import {
		capFirstChar,
		getErrorOrInfoMessageForMinAndMax,
		makeNumbersPrimaryAndBold,
	} from '../../../../helpers/utilities';
	//Constants
	import { DEVICE_MAX_POWER_DEFAULTS } from '../../../../constants';
	//Stores
		//Design system components
	import { Text, Button, Checkbox, Input, Toggle, toast } from '@Shelly/shelly-design-system';
	import { restrictDecimalNumberInput } from 'src/helpers/number_input_helpers';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';

	$: settingsStore = getDeviceSettings(id);
	export let device;

	const id = real_id(device.id);

	let settings = $settingsStore;

	const min = 0;

	const max = DEVICE_MAX_POWER_DEFAULTS[device.type];

	let value, checked, cacheValue, cacheChecked;
	let lastCustomValue = 0;
	let loading = false;
	let localIp = get_local_ip(id);

	const translationsWithWatts = true;
	$: inputData = getErrorOrInfoMessageForMinAndMax(value, min, max, translationsWithWatts);

	onMount(() => {
		value = $settingsStore.max_power;
		checked = +value > 0;
		cacheChecked = checked;
		cacheValue = value;
		lastCustomValue = checked ? value : max;
	});

	const savePower = async () => {
		if (cacheChecked !== checked || cacheValue !== value) {
			let res;
			loading = true;

			if (localIp) {
				res = await localReq(device, `${localIp}/settings`, 'post', {
					max_power: value,
				});
			} else {
				res = await postReq(`/device/relay/settings/power_max`, {
					value: value,
					id,
				});
			}

			if (res.isok) {
				settings.max_power = value;
				devices_settings.update_devices_settings(device.id, settings);
				checked = +value > 0;
				if (+value > 0) {
					lastCustomValue = value;
				}
				if (value == '0') {
					toast.open({
						type: 'success',
						title: $format('power_protection_disable_custom', {
							default: `${capFirstChar(device.type)} custom power protection was disabled.`,
						}),
					});
				} else {
					toast.open({
						type: 'success',
						title: $format('power_protection_save_scc_toast', {
							values: { value },
							default: `${capFirstChar(device.type)} custom power protection was set to ${value}W.`,
						}),
					});
				}
				cacheChecked = checked;
				cacheValue = value;
			} else {
				checked = cacheChecked;
				value = cacheValue;
				toast.open({
					type: 'danger',
					title: $format('power_protection_save_err_toast'),
				});
			}
			loading = false;
		}
	};

	const handleToggler = async () => {
		if (loading) return;

		checked = !checked;
		if (checked) {
			value = +lastCustomValue > 0 ? lastCustomValue : max;
		} else {
			if (+value > 0) {
				lastCustomValue = value;
			}
			value = 0;
		}

		if ($showNewLayout) {
			await savePower();
		}
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout && checked) {
			stickyBottomAction.set({ onClick: savePower, loading, disabled: loading });
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

{#if $showNewLayout}
	<Toggle
		size="m"
		class="mt-s-plus mb-s-plus"
		{checked}
		disabled={loading}
		on:click={handleToggler}
		id="toggle-cw3b2iq"
	>
		{$format('custom_max_power_protection')}
	</Toggle>
{:else}
	<Checkbox size="m" class="mb-s" bind:checked on:click={handleToggler} id="checkbox-xrfgzz0">
		{$format('custom_max_power_protection')}
	</Checkbox>
{/if}
{#if checked}
	<Text styleType="controls-small" class="mb-s">
		{@html makeNumbersPrimaryAndBold(
			`${$format('device_turn_off_label', {
				values: { value },
				default: `Device will turn off when power reaches ${value} W.`,
			})}`,
		)}
	</Text>
	<Input
		on:input={(e) => restrictDecimalNumberInput(e, 2)}
		size="m"
		disabled={!checked}
		bind:value
		type={'number'}
		info={inputData.info}
		error={inputData.error}
		{min}
		{max}
		step={1}
		on:enter={savePower}
		id="input-aombdgm"
		aria-label={$format('max_power_threshold', {
			default: 'Max power threshold',
		})}
	/>
	{#if !$showNewLayout}
		<Button class="mt-s" {loading} disabled={loading} size="m" width="full" on:click={savePower}>
			{$format('button_save')}
		</Button>
	{/if}
{/if}
